import React, {useEffect, useState} from "react"
import {isUndefined} from "lodash"
import {useToasts} from "react-toast-notifications"

import {EditableAnnualIncomeField} from "../ApplicationAdditionalInformationFields/AnnualIncomeField"
import {EditableOccupationField} from "../ApplicationAdditionalInformationFields/OccupationField"
import {EditableSourceOfIncomeField} from "../ApplicationAdditionalInformationFields/SourceOfIncomeField"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {CustomerBeneficialOwner, patchCustomerBeneficialOwner} from "../../resources/customerBeneficialOwner"
import {useAccessToken} from "../../services/auth"

type BeneficialOwnerAdditionalInformationRowProps = {
	customerId: string
	beneficialOwner: CustomerBeneficialOwner
	archivedClassName: string
	isEditing: string | undefined
	setIsEditing: (value: string | undefined) => void
	setIsUpdating: (isUpdating: boolean) => void
	isAllowed: boolean
	refresh: () => void
}

export function BeneficialOwnerAdditionalInformationRow({
	customerId,
	beneficialOwner,
	archivedClassName,
	isEditing,
	setIsEditing,
	setIsUpdating,
	isAllowed,
	refresh,
}: BeneficialOwnerAdditionalInformationRowProps) {
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const [updateBeneficialOwnerState, patchBeneficialOwner] = useAsyncResultIdle(patchCustomerBeneficialOwner)
	const [occupation, setOccupation] = useState(beneficialOwner.attributes.occupation)
	const [annualIncome, setAnnualIncome] = useState(beneficialOwner.attributes.annualIncome)
	const [sourceOfIncome, setSourceOfIncome] = useState(beneficialOwner.attributes.sourceOfIncome)

	useEffect(() => {
		if (updateBeneficialOwnerState.isOk()) {
			addToast("Beneficial Owner Updated Successfully", {appearance: "success"})
			setIsUpdating(false)
			refresh()
		} else if (updateBeneficialOwnerState.isErr()) {
			addToast(updateBeneficialOwnerState.error.errors[0].detail || updateBeneficialOwnerState.error.errors[0].title, {
				appearance: "error",
			})
			setIsUpdating(false)
			refresh()
		}
	}, [updateBeneficialOwnerState])

	if (!occupation && !annualIncome && !sourceOfIncome) {
		return null
	}

	const occupationEditKey = `BeneficialOwnerOccupation-${beneficialOwner.id}`
	const annualIncomeEditKey = `BeneficialOwnerAnnualIncome-${beneficialOwner.id}`
	const sourceOfIncomeEditKey = `BeneficialOwnerSourceOfIncome-${beneficialOwner.id}`

	return (
		<div className={"bo-more-info-inner-container"}>
			<div className={"bo-more-info-label"}>
				{beneficialOwner.attributes.fullName.first} {beneficialOwner.attributes.fullName.last}
			</div>
			<EditableOccupationField
				occupation={occupation}
				setOccupation={setOccupation}
				isEditing={isEditing === occupationEditKey}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing(occupationEditKey)}
				onCancel={refresh}
				update={() => {
					patchBeneficialOwner(accessToken, customerId, beneficialOwner.id, {occupation})
					setIsUpdating(true)
				}}
				className={archivedClassName}
			/>
			<EditableAnnualIncomeField
				annualIncome={annualIncome}
				setAnnualIncome={setAnnualIncome}
				isEditing={isEditing === annualIncomeEditKey}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing(annualIncomeEditKey)}
				onCancel={refresh}
				update={() => {
					patchBeneficialOwner(accessToken, customerId, beneficialOwner.id, {annualIncome})
					setIsUpdating(true)
				}}
				className={archivedClassName}
			/>
			<EditableSourceOfIncomeField
				sourceOfIncome={sourceOfIncome}
				setSourceOfIncome={setSourceOfIncome}
				isEditing={isEditing === sourceOfIncomeEditKey}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing(sourceOfIncomeEditKey)}
				onCancel={refresh}
				update={() => {
					patchBeneficialOwner(accessToken, customerId, beneficialOwner.id, {sourceOfIncome})
					setIsUpdating(true)
				}}
				className={archivedClassName}
			/>
		</div>
	)
}
